import React, { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { formatPrice } from "@/components/ProductCard";
import { toast } from "sonner";
import { CLAIM_REASONS, claimStatusMeta, CLOSED_STATES } from "@/lib/claims";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Warning, X, UploadSimple, CaretDown, PaperPlaneRight, ChatCircleDots, DownloadSimple,
  CheckCircle, Wallet, Clock, ArrowClockwise, ShieldWarning,
} from "@phosphor-icons/react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const inputCls = "w-full border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";

function timeLeft(deadline) {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return "vencido";
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 6e4);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}

export function NewClaimModal({ order, onClose, onCreated }) {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files.slice(0, 5 - photos.length)) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/upload/claim", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, res.data.path]);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir la foto"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!reason) { toast.error("Selecciona un motivo"); return; }
    if (!description.trim()) { toast.error("Describe el problema"); return; }
    setSaving(true);
    try {
      const res = await api.post("/claims", { order_id: order.id, reason, description: description.trim(), photos });
      toast.success(`Reclamo creado · Folio ${res.data.folio}`);
      onCreated?.(res.data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo crear el reclamo"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-foreground/40 flex items-start justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-lg nb-shadow-lg w-full max-w-lg my-8" data-testid="new-claim-modal">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h2 className="text-lg font-extrabold tracking-tight flex items-center gap-2"><Warning weight="bold" size={20} className="text-primary" /> Abrir reclamo</h2>
          <button onClick={onClose} className="nb-btn bg-secondary p-1.5"><X weight="bold" size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-secondary/40 rounded-md p-3 text-sm">
            <p className="font-semibold">Pedido {order.order_number || order.id.slice(0, 8)}</p>
            <p className="text-xs text-muted-foreground">{order.items?.length} producto(s) · {formatPrice(order.total)}</p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Motivo</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="claim-reason" className="rounded-md text-sm mt-1"><SelectValue placeholder="Selecciona un motivo" /></SelectTrigger>
              <SelectContent className="rounded-md">{CLAIM_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Explica el problema</label>
            <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="claim-description"
                      placeholder="Cuéntanos qué pasó con tu pedido…" className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide">Evidencia (fotos, opcional · máx. 5)</label>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" />
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={fileUrl(p)} alt="" className="w-14 h-14 rounded object-cover border border-black/10" />
                  <button onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-white rounded-full nb-shadow p-0.5"><X weight="bold" size={11} /></button>
                </div>
              ))}
              {photos.length < 5 && (
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="claim-upload-photo"
                        className="nb-btn bg-secondary px-3 py-2 text-sm flex items-center gap-1"><UploadSimple weight="bold" size={15} /> {uploading ? "Subiendo…" : "Agregar fotos"}</button>
              )}
            </div>
          </div>
          <button onClick={submit} disabled={saving} data-testid="submit-claim-button" className="w-full nb-btn bg-primary text-primary-foreground py-2.5 hover:brightness-110 disabled:opacity-50">{saving ? "Enviando…" : "Enviar reclamo"}</button>
        </div>
      </div>
    </div>
  );
}

function Timeline({ messages = [] }) {
  return (
    <div className="space-y-2" data-testid="claim-timeline">
      {messages.map((m) => {
        if (m.system) return <p key={m.id} className="text-[11px] text-muted-foreground text-center py-1">· {m.text} ·</p>;
        const mine = m.author_role === "cliente";
        return (
          <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
              <p className="text-[10px] font-bold opacity-80 mb-0.5">{m.author_role === "admin" ? "Todito Ventas" : m.author_name}</p>
              <p>{m.text}</p>
              <p className="text-[9px] opacity-60 mt-0.5">{new Date(m.created_at).toLocaleString("es-MX")}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GuideBox({ title, guide }) {
  if (!guide) return null;
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm">
      <p className="font-semibold text-indigo-800">{title}</p>
      <p className="text-xs text-indigo-700 mt-0.5"><b>{guide.carrier}</b> · Guía {guide.tracking_number}</p>
      {guide.instructions && <p className="text-xs text-indigo-700 mt-1">{guide.instructions}</p>}
      <div className="flex gap-3 mt-2">
        {guide.label_url && <a href={fileUrl(guide.label_url)} target="_blank" rel="noreferrer" data-testid="download-guide" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"><DownloadSimple weight="bold" size={14} /> Descargar guía</a>}
        {guide.track_url && <a href={guide.track_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Rastrear</a>}
      </div>
    </div>
  );
}

function ClaimDetail({ claim, onUpdate }) {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const closed = CLOSED_STATES.includes(claim.status);

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try { const r = await api.post(`/claims/${claim.id}/message`, { text: msg.trim() }); setMsg(""); onUpdate(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSending(false); }
  };

  const accept = async () => {
    try { const r = await api.post(`/claims/${claim.id}/accept-exchange`); toast.success("Cambio aceptado"); onUpdate(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const showReturnHint = ["cambio_devolucion", "saldo_devolucion", "reembolso_devolucion"].includes(claim.status);

  return (
    <div className="border-t border-black/10 p-4 space-y-4">
      {claim.status === "esperando_cliente" && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-700 flex items-center gap-2">
          <Clock weight="bold" size={15} /> Debes responder para continuar. Tiempo restante: <b>{timeLeft(claim.reply_deadline)}</b>. Si no respondes en 24 h el reclamo se cerrará.
        </div>
      )}
      {claim.status === "cerrado_sin_respuesta" && (
        <div className="rounded-lg bg-gray-100 border border-gray-200 px-3 py-2 text-xs text-gray-600">Este reclamo se cerró porque no hubo respuesta de tu parte dentro de las 24 horas.</div>
      )}

      {/* Motivo + evidencia */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Motivo</p>
        <p className="text-sm">{claim.reason}</p>
        {claim.description && <p className="text-sm text-muted-foreground mt-1">{claim.description}</p>}
        {(claim.photos || []).length > 0 && (
          <div className="flex gap-2 mt-2 flex-wrap">
            {claim.photos.map((p, i) => <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt="" className="w-16 h-16 rounded object-cover border border-black/10" /></a>)}
          </div>
        )}
      </div>

      {claim.verdict === "cambio" && claim.status === "cambio_por_aceptar" && (
        <div className="rounded-lg bg-violet-50 border border-violet-200 p-3">
          <p className="text-sm text-violet-800 font-semibold">Te proponemos un cambio de producto</p>
          {claim.verdict_note && <p className="text-xs text-violet-700 mt-0.5">{claim.verdict_note}</p>}
          <button onClick={accept} data-testid="accept-exchange-button" className="mt-2 nb-btn bg-violet-600 text-white px-4 py-1.5 text-sm hover:brightness-110 flex items-center gap-1"><CheckCircle weight="bold" size={15} /> Aceptar cambio</button>
        </div>
      )}

      <GuideBox title="Guía de devolución — envía el producto" guide={claim.status !== "cambio_por_aceptar" && claim.status !== "cambio_aceptado" ? claim.return_guide : null} />
      {showReturnHint && <p className="text-xs text-muted-foreground -mt-2">Descarga la guía, empaca el producto y entrégalo en la paquetería indicada.</p>}
      <GuideBox title="Guía del producto de reemplazo" guide={claim.replacement_guide} />

      {claim.store_credit_amount > 0 && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2"><Wallet weight="bold" size={16} /> Se acreditó un saldo a favor de <b>{formatPrice(claim.store_credit_amount)}</b>.</div>
      )}
      {claim.refund && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800">
          Reembolso de <b>{formatPrice(claim.refund.amount)}</b> · {claim.refund.date}{claim.refund.reference ? ` · Ref. ${claim.refund.reference}` : ""}.
        </div>
      )}

      {/* Conversación */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><ChatCircleDots weight="bold" size={14} /> Historial del reclamo</p>
        <Timeline messages={claim.messages} />
      </div>

      {!closed && (
        <div className="flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Escribe un mensaje…" data-testid="claim-message-input" className={inputCls} />
          <button onClick={send} disabled={sending} data-testid="claim-send-button" className="nb-btn bg-primary text-primary-foreground px-4 hover:brightness-110 disabled:opacity-50"><PaperPlaneRight weight="bold" size={16} /></button>
        </div>
      )}
    </div>
  );
}

export function ClaimsTab() {
  const [claims, setClaims] = useState([]);
  const [credit, setCredit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => {
    Promise.all([api.get("/buyer/claims"), api.get("/buyer/credit")])
      .then(([c, cr]) => { setClaims(c.data); setCredit(cr.data.balance || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const updateOne = (updated) => setClaims((list) => list.map((c) => (c.id === updated.id ? updated : c)));

  if (loading) return <p className="text-muted-foreground">Cargando…</p>;

  return (
    <div className="space-y-3" data-testid="claims-tab">
      <div className="bg-white rounded-lg nb-shadow p-4 flex items-center gap-3" data-testid="store-credit-card">
        <span className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"><Wallet weight="fill" size={20} className="text-emerald-600" /></span>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">Mi saldo a favor</p>
          <p className="text-xl font-extrabold tracking-tight">{formatPrice(credit)}</p>
        </div>
        <p className="text-[11px] text-muted-foreground ml-auto max-w-[180px] text-right">Se aplica automáticamente en tu próxima compra.</p>
      </div>

      {claims.length === 0 ? (
        <div className="bg-white rounded-lg nb-shadow p-10 text-center">
          <ShieldWarning weight="bold" size={36} className="mx-auto text-muted-foreground" />
          <p className="text-lg font-bold mt-3">No tienes reclamos</p>
          <p className="text-sm text-muted-foreground mt-1">Puedes abrir un reclamo desde “Mis compras” si tuviste un problema con un pedido.</p>
        </div>
      ) : claims.map((c) => {
        const meta = claimStatusMeta(c.status);
        return (
          <div key={c.id} className="bg-white rounded-lg nb-shadow overflow-hidden" data-testid={`claim-${c.id}`}>
            <button onClick={() => setOpen(open === c.id ? null : c.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-secondary/30">
              <div className="flex -space-x-3">
                {(c.items || []).slice(0, 3).map((it, i) => (
                  <img key={i} src={it.product_image ? fileUrl(it.product_image) : PLACEHOLDER} alt="" className="w-10 h-10 rounded-md object-cover border-2 border-white" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
                ))}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">Folio {c.folio}</p>
                <p className="text-xs text-muted-foreground">Pedido {c.order_number} · {new Date(c.created_at).toLocaleDateString("es-MX")} · {c.reason}</p>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${meta.cls}`}>{meta.label}</span>
              <CaretDown weight="bold" size={16} className={`transition-transform ${open === c.id ? "rotate-180" : ""}`} />
            </button>
            {open === c.id && <ClaimDetail claim={c} onUpdate={updateOne} />}
          </div>
        );
      })}
    </div>
  );
}
